/**
 * AttackBanner — floating "FROM X → TARGET Y" banner that anchors the
 * attack flow visually at the top of the map.
 *
 * Visibility:
 *   - `attackState.kind === 'sourceSelected'` →
 *       [FROM Brazil]  →  click an enemy below
 *   - `attackState.kind === 'targetSelected'` →
 *       [FROM Brazil]  →  [TARGET Mexico]  · commit cubes
 *   - otherwise hidden.
 *
 * Style: cream-paper background, wax-red dashed border, mono uppercase —
 * matches the same chalk-on-paper vocabulary the rest of the map uses.
 *
 * Position: anchored at the top-center of its parent (which the caller
 * sets up as `position: relative`). Sibling to the map SVG, NOT inside the
 * `TransformComponent` — so it stays put while the player pans/zooms.
 */

import { type ReactNode } from 'react';
import type { AttackState } from '@qd/protocol';
import { surfaces } from '@qd/design-tokens';
import { useIsMobile } from '../../hooks/useIsMobile';

interface AttackBannerProps {
  readonly attackState: AttackState;
  readonly sourceName: string | null;
  readonly targetName: string | null;
}

const MONO_FONT = "'Departure Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

export function AttackBanner({
  attackState,
  sourceName,
  targetName,
}: AttackBannerProps): ReactNode {
  const isMobile = useIsMobile();
  const visible =
    attackState.kind === 'sourceSelected' || attackState.kind === 'targetSelected';
  if (!visible) return null;

  const showTarget = attackState.kind === 'targetSelected';
  const fromText = (sourceName ?? '???').toUpperCase();
  const targetText = (targetName ?? '???').toUpperCase();

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 5,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        // Tighter padding on mobile to keep the banner from spilling
        // past the map's visible width with long territory names.
        padding: isMobile ? '6px 10px' : '8px 14px',
        background: surfaces.paper,
        border: `1.5px dashed ${surfaces.waxRed}`,
        borderRadius: 4,
        boxShadow: '0 4px 14px rgba(42,46,51,0.18)',
        fontFamily: MONO_FONT,
        fontSize: 12,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: surfaces.ink,
        // On mobile, a single long territory name (e.g. "REGRESSION
        // MITIGATION SUITE") can blow past 60vw with `whiteSpace:
        // nowrap`. Cap the banner's width and let the chip values
        // truncate with an ellipsis instead. Desktop keeps the
        // original single-line nowrap behaviour.
        whiteSpace: 'nowrap',
        maxWidth: isMobile ? '92vw' : undefined,
        userSelect: 'none',
      }}
    >
      <Chip label="FROM" value={fromText} accent="ink" truncate={isMobile} />
      <Arrow active={showTarget} />
      {showTarget ? (
        <>
          <Chip label="TARGET" value={targetText} accent="wax" truncate={isMobile} />
          {!isMobile ? <span style={{ opacity: 0.7 }}>· COMMIT CUBES</span> : null}
        </>
      ) : !isMobile ? (
        <span style={{ opacity: 0.85 }}>CLICK AN ENEMY BELOW ↓</span>
      ) : null}
    </div>
  );
}

interface ChipProps {
  readonly label: string;
  readonly value: string;
  readonly accent: 'ink' | 'wax';
  /** When true, cap the chip's value width and ellipsis-truncate. */
  readonly truncate?: boolean;
}

function Chip({ label, value, accent, truncate = false }: ChipProps): ReactNode {
  const color = accent === 'wax' ? surfaces.waxRed : surfaces.chalk;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 6,
        padding: '2px 8px',
        border: `1px solid ${color}`,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.35)',
        // Allow this flex item to shrink below its intrinsic width so
        // the inner truncation actually kicks in.
        minWidth: 0,
        flexShrink: 1,
      }}
    >
      <span
        style={{
          fontSize: 9,
          opacity: 0.7,
          letterSpacing: '0.18em',
          color: surfaces.ink,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.12em',
          color: surfaces.ink,
          // Mobile: cap at 32vw so two chips + label + arrow fit within
          // 92vw with the dashed border and gaps.
          maxWidth: truncate ? '32vw' : undefined,
          overflow: truncate ? 'hidden' : undefined,
          textOverflow: truncate ? 'ellipsis' : undefined,
          whiteSpace: truncate ? 'nowrap' : undefined,
          minWidth: 0,
        }}
      >
        {value}
      </span>
    </span>
  );
}

function Arrow({ active }: { readonly active: boolean }): ReactNode {
  return (
    <span
      aria-hidden="true"
      style={{
        fontSize: 16,
        lineHeight: 1,
        color: active ? surfaces.waxRed : surfaces.chalk,
        opacity: active ? 1 : 0.6,
        fontWeight: 700,
      }}
    >
      →
    </span>
  );
}
